// src/observer/audioOnce.ts
// Clean, AudioController-based narration start helper

import { AudioController } from "@/utils/audioController";

/**
 * Plays a narration <audio> element once per section,
 * respecting global mute + unlock state.
 *
 * Returns Promise<boolean>:
 *   true  = playback started
 *   false = playback blocked or failed
 */
export async function playAudioOnce(audio: HTMLAudioElement): Promise<boolean> {
  const section = audio.closest("section[data-section-index]") as HTMLElement | null;
  const idx = section?.getAttribute("data-section-index") ?? null;

  // 1) Already played? No double-triggering.
  if (audio.dataset.scPlayed === "1") {
    return true;
  }

  // 2) If globally muted → DO NOT auto-play
  if (AudioController.isMuted()) {
    dispatchAudioError("blocked-muted");
    return false;
  }

  // 3) If audio isn't unlocked yet → Chrome/iOS will reject
  if (!AudioController.isUnlocked()) {
    dispatchAudioError("blocked-locked");
    return false;
  }

  // 4) Normalize <audio> attributes for reliable mobile behavior
  normalizeAudioElement(audio);

  try {
    // 5) Attempt playback using native HTML API
    await audio.play();

    // 6) Success → mark and notify HUD
    audio.dataset.scPlayed = "1";
    if (section) section.dataset.audioStarted = "1";

    document.dispatchEvent(
      new CustomEvent("sc:audio-started", {
        detail: { audio, section, sectionIndex: idx }
      })
    );

    return true;
  } catch (err) {
    dispatchAudioError(err);
    return false;
  }

  // Helper to normalize attributes.
  function normalizeAudioElement(a: HTMLAudioElement) {
    if (!a.hasAttribute("preload")) a.preload = "auto";
    a.setAttribute("playsinline", "true");
    a.setAttribute("webkit-playsinline", "true");
    a.setAttribute("crossorigin", "anonymous");

    if (!a.hasAttribute("data-role")) {
      a.setAttribute("data-role", "narration");
    }
  }

  function dispatchAudioError(error: unknown) {
    document.dispatchEvent(
      new CustomEvent("sc:audio-error", {
        detail: { audio, section, sectionIndex: idx, error }
      })
    );
  }
}